import { Preferences } from '@capacitor/preferences';
import { BACKUP_KEY, MemoryStorage, SAVE_KEY, type KeyValueStorage } from '../meta/save/store';
import { isNative } from './native';

let active: KeyValueStorage | null = null;

/** The platform's storage, ready for synchronous use (call {@link initStorage} first natively). */
export function platformStorage(): KeyValueStorage {
  active ??= createWebStorage();
  return active;
}

/**
 * Prepares storage before the game boots. Natively the save lives in Capacitor Preferences
 * (UserDefaults / SharedPreferences), which the OS never evicts the way it can evict a WebView's
 * localStorage. Preferences is async, so the keys are read once up front and kept in memory;
 * writes go to memory immediately and to Preferences in the background.
 */
export async function initStorage(): Promise<void> {
  if (!isNative()) return;
  const cache = new Map<string, string>();
  for (const key of [SAVE_KEY, BACKUP_KEY]) {
    const { value } = await Preferences.get({ key });
    if (value !== null) cache.set(key, value);
  }
  // First native launch after an update that used localStorage: carry the save over.
  if (!cache.has(SAVE_KEY)) {
    const legacy = createWebStorage().get(SAVE_KEY);
    if (legacy !== null) {
      cache.set(SAVE_KEY, legacy);
      await Preferences.set({ key: SAVE_KEY, value: legacy });
    }
  }
  active = {
    get: (key) => cache.get(key) ?? null,
    set: (key, value) => {
      cache.set(key, value);
      void Preferences.set({ key, value });
    },
  };
}

/**
 * Web key-value storage (localStorage). Falls back to memory when storage is blocked (private
 * mode, sandboxed iframes) so the game still runs; progress just won't persist.
 */
export function createWebStorage(): KeyValueStorage {
  try {
    const probe = '__lm_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
  } catch {
    return new MemoryStorage();
  }
  return {
    get: (key) => {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // Quota or permission error: keep playing; the next write may succeed.
      }
    },
  };
}
